import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import {
  Plus,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Music2,
  Settings as SettingsIcon,
  Save,
  X,
} from 'lucide-react';

// Condition type values (labels come from translations)
const CONDITION_TYPE_VALUES = [
  { value: 'url_contains', category: 'page' },
  { value: 'url_equals', category: 'page' },
  { value: 'url_starts_with', category: 'page' },
  { value: 'time_on_page', category: 'visitor' },
  { value: 'returning_visitor', category: 'visitor' },
  { value: 'new_visitor', category: 'visitor' },
  { value: 'message_contains', category: 'intent' },
  { value: 'browser_language', category: 'language' },
  { value: 'referrer_contains', category: 'campaign' },
  { value: 'utm_parameter', category: 'campaign' },
];

// Action type values (labels come from translations)
const ACTION_TYPE_VALUES = [
  { value: 'escalate_to_human', category: 'routing' },
  { value: 'notify_team', category: 'routing' },
  { value: 'transfer_persona', category: 'routing' },
  { value: 'switch_tone', category: 'ai' },
  { value: 'send_greeting', category: 'ai' },
  { value: 'ask_follow_up', category: 'ai' },
  { value: 'ask_for_email', category: 'ai' },
  { value: 'send_link', category: 'ai' },
  { value: 'trigger_instructions', category: 'ai' },
  { value: 'open_widget_tab', category: 'ui' },
  { value: 'push_url', category: 'ui' },
  { value: 'show_suggestion', category: 'ui' },
];

// Tone option values (labels come from translations)
const TONE_OPTIONS = [
  'friendly',
  'professional',
  'casual',
  'formal',
  'concise',
  'detailed',
  'empathetic',
  'directive',
];

// Persona option values (labels come from translations)
const PERSONA_OPTIONS = [
  'support',
  'sales',
  'guide',
  'neutral',
  'technical',
  'friendly',
];

interface Condition {
  type: string;
  value?: string | number;
  operator?: string;
}

interface Action {
  type: string;
  value?: string;
  params?: Record<string, any>;
}

interface Orchestration {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  priority: number;
  conditions: Condition[];
  actions: Action[];
  next_orchestration_id?: string | null;
  tone_override?: string;
  persona_override?: string;
  custom_instructions?: string;
}

interface GlobalInstructions {
  default_tone: string;
  default_persona: string;
  custom_instructions?: string;
}

interface Warning {
  type: string;
  priority: number;
  orchestrations: string[];
  message: string;
}

export default function Orchestrations() {
  const { t } = useTranslation();
  const [orchestrations, setOrchestrations] = useState<Orchestration[]>([]);
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [globalInstructions, setGlobalInstructions] = useState<GlobalInstructions | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<Orchestration>>({
    name: '',
    description: '',
    enabled: true,
    priority: 0,
    conditions: [{ type: 'url_contains' }],
    actions: [{ type: 'send_greeting' }],
  });

  useEffect(() => {
    loadOrchestrations();
    loadGlobalInstructions();
  }, []);

  const loadOrchestrations = async () => {
    try {
      const data: any = await api.getOrchestrations();
      // Handle new response structure with warnings
      if (data && typeof data === 'object' && 'orchestrations' in data) {
        setOrchestrations(data.orchestrations as Orchestration[]);
        setWarnings(data.warnings || []);
      } else {
        // Fallback for old response structure (just array)
        setOrchestrations(data as Orchestration[]);
        setWarnings([]);
      }
    } catch (error) {
      console.error('Failed to load orchestrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGlobalInstructions = async () => {
    try {
      const data = await api.getGlobalInstructions();
      setGlobalInstructions(data);
    } catch (error) {
      console.error('Failed to load global instructions:', error);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      enabled: true,
      priority: 0,
      conditions: [{ type: 'url_contains' }],
      actions: [{ type: 'send_greeting' }],
    });
    setShowForm(true);
  };

  const handleEdit = (orchestration: Orchestration) => {
    setEditingId(orchestration.id);
    setFormData(orchestration);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.updateOrchestration(editingId, formData as Orchestration);
      } else {
        await api.createOrchestration(formData as Orchestration);
      }
      setShowForm(false);
      loadOrchestrations();
    } catch (error) {
      console.error('Failed to save orchestration:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm(t('orchestrations.deleteConfirm'))) {
      try {
        await api.deleteOrchestration(id);
        loadOrchestrations();
      } catch (error) {
        console.error('Failed to delete orchestration:', error);
      }
    }
  };

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    try {
      const orchestration = orchestrations.find(o => o.id === id);
      if (orchestration) {
        await api.updateOrchestration(id, { ...orchestration, enabled: !enabled });
        loadOrchestrations();
      }
    } catch (error) {
      console.error('Failed to toggle orchestration:', error);
    }
  };

  const handleSaveGlobalInstructions = async () => {
    try {
      if (globalInstructions) {
        await api.updateGlobalInstructions(globalInstructions);
        setShowGlobalSettings(false);
      }
    } catch (error) {
      console.error('Failed to save global instructions:', error);
    }
  };

  const addCondition = () => {
    setFormData({
      ...formData,
      conditions: [...(formData.conditions || []), { type: 'url_contains' }],
    });
  };

  const removeCondition = (index: number) => {
    const newConditions = [...(formData.conditions || [])];
    newConditions.splice(index, 1);
    setFormData({ ...formData, conditions: newConditions });
  };

  const updateCondition = (index: number, field: string, value: any) => {
    const newConditions = [...(formData.conditions || [])];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setFormData({ ...formData, conditions: newConditions });
  };

  const addAction = () => {
    setFormData({
      ...formData,
      actions: [...(formData.actions || []), { type: 'send_greeting' }],
    });
  };

  const removeAction = (index: number) => {
    const newActions = [...(formData.actions || [])];
    newActions.splice(index, 1);
    setFormData({ ...formData, actions: newActions });
  };

  const updateAction = (index: number, field: string, value: any) => {
    const newActions = [...(formData.actions || [])];
    newActions[index] = { ...newActions[index], [field]: value };
    setFormData({ ...formData, actions: newActions });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Music2 className="w-6 h-6 text-gray-900" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {t('orchestrations.title')}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">
                  {t('orchestrations.subtitle')}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowGlobalSettings(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                <SettingsIcon className="w-4 h-4" />
                <span>{t('orchestrations.globalSettings')}</span>
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
              >
                <Plus className="w-4 h-4" />
                <span>{t('orchestrations.create')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Priority Conflict Warnings */}
        {warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-800 mb-2">
                  {t('orchestrations.priorityConflicts')}
                </h3>
                <div className="space-y-2">
                  {warnings.map((warning, index) => (
                    <div key={index} className="text-sm text-yellow-700">
                      <p>{warning.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orchestrations List */}
        {orchestrations.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Music2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('orchestrations.noOrchestrations')}
            </h3>
            <p className="text-gray-600 mb-6">{t('orchestrations.getStarted')}</p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
            >
              <Plus className="w-4 h-4" />
              <span>{t('orchestrations.createFirst')}</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {orchestrations.map((orch) => (
              <div
                key={orch.id}
                className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{orch.name}</h3>
                      <span className="text-sm text-gray-500">{t('orchestrations.priorityLabel')} {orch.priority}</span>
                      {orch.enabled ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          {t('common.enabled')}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                          {t('common.disabled')}
                        </span>
                      )}
                    </div>
                    {orch.description && (
                      <p className="text-sm text-gray-600 mb-3">{orch.description}</p>
                    )}

                    {/* Conditions */}
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                        {t('orchestrations.conditions')} (IF)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {orch.conditions.map((cond, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 text-xs rounded bg-blue-50 text-blue-700"
                          >
                            {t(`orchestrations.conditionTypes.${cond.type}`, cond.type)}
                            {cond.value && `: ${cond.value}`}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                        {t('orchestrations.actions')} (THEN)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {orch.actions.map((action, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700"
                          >
                            {t(`orchestrations.actionTypes.${action.type}`, action.type)}
                            {action.value && `: ${action.value}`}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Next orchestration */}
                    {orch.next_orchestration_id && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                          {t('orchestrations.nextStep')} (NEXT)
                        </p>
                        <span className="px-2 py-1 text-xs rounded bg-yellow-50 text-yellow-700">
                          →{' '}
                          {orchestrations.find((o) => o.id === orch.next_orchestration_id)
                            ?.name || 'Unknown'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleToggleEnabled(orch.id, orch.enabled)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                      title={orch.enabled ? t('common.disable') : t('common.enable')}
                    >
                      {orch.enabled ? (
                        <PowerOff className="w-4 h-4" />
                      ) : (
                        <Power className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(orch)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                      title={t('common.edit')}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(orch.id)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingId
                      ? t('orchestrations.editOrchestration')
                      : t('orchestrations.createOrchestration')}
                  </h2>
                  <button
                    onClick={() => setShowForm(false)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('orchestrations.name')}
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    placeholder={t('orchestrations.namePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('orchestrations.description')}
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    rows={2}
                    placeholder={t('orchestrations.descriptionPlaceholder')}
                  />
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('orchestrations.priority')}
                    </label>
                    <input
                      type="number"
                      value={formData.priority || 0}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: parseInt(e.target.value) })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.enabled}
                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                        className="w-4 h-4 text-black rounded focus:ring-black"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {t('common.enabled')}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Conditions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('orchestrations.conditions')} (IF)
                    </label>
                    <button
                      onClick={addCondition}
                      className="text-sm text-black hover:underline"
                    >
                      + {t('orchestrations.addCondition')}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {formData.conditions?.map((cond, idx) => (
                      <div key={idx} className="flex gap-2">
                        <select
                          value={cond.type}
                          onChange={(e) => updateCondition(idx, 'type', e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        >
                          {CONDITION_TYPE_VALUES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {t(`orchestrations.conditionTypes.${type.value}`, type.value)}
                            </option>
                          ))}
                        </select>
                        {cond.type === 'time_on_page' && (
                          <select
                            value={cond.operator || 'greater_than'}
                            onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          >
                            <option value="greater_than">{t('orchestrations.greaterThan')}</option>
                            <option value="less_than">{t('orchestrations.lessThan')}</option>
                            <option value="equals">{t('orchestrations.equals')}</option>
                          </select>
                        )}
                        {!['returning_visitor', 'new_visitor'].includes(cond.type) && (
                          <input
                            type="text"
                            value={cond.value || ''}
                            onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('orchestrations.value')}
                          />
                        )}
                        <button
                          onClick={() => removeCondition(idx)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('orchestrations.actions')} (THEN)
                    </label>
                    <button onClick={addAction} className="text-sm text-black hover:underline">
                      + {t('orchestrations.addAction')}
                    </button>
                  </div>
                  <div className="space-y-3">
                    {formData.actions?.map((action, idx) => (
                      <div key={idx} className="flex gap-2">
                        <select
                          value={action.type}
                          onChange={(e) => updateAction(idx, 'type', e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        >
                          {ACTION_TYPE_VALUES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {t(`orchestrations.actionTypes.${type.value}`, type.value)}
                            </option>
                          ))}
                        </select>
                        {['switch_tone', 'send_greeting', 'ask_follow_up', 'send_link', 'push_url', 'show_suggestion', 'transfer_persona'].includes(
                          action.type
                        ) && (
                          <input
                            type="text"
                            value={action.value || ''}
                            onChange={(e) => updateAction(idx, 'value', e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={
                              action.type === 'switch_tone'
                                ? t('orchestrations.toneValue')
                                : t('orchestrations.value')
                            }
                          />
                        )}
                        <button
                          onClick={() => removeAction(idx)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tone/Persona Overrides */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('orchestrations.toneOverride')}
                    </label>
                    <select
                      value={formData.tone_override || ''}
                      onChange={(e) => setFormData({ ...formData, tone_override: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="">{t('orchestrations.noOverride')}</option>
                      {TONE_OPTIONS.map((tone) => (
                        <option key={tone} value={tone}>
                          {t(`orchestrations.tones.${tone}`, tone)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('orchestrations.personaOverride')}
                    </label>
                    <select
                      value={formData.persona_override || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, persona_override: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="">{t('orchestrations.noOverride')}</option>
                      {PERSONA_OPTIONS.map((persona) => (
                        <option key={persona} value={persona}>
                          {t(`orchestrations.personas.${persona}`, persona)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Next Orchestration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('orchestrations.nextOrchestration')} (NEXT)
                  </label>
                  <select
                    value={formData.next_orchestration_id || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, next_orchestration_id: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                  >
                    <option value="">{t('orchestrations.noNext')}</option>
                    {orchestrations
                      .filter((o) => o.id !== editingId)
                      .map((orch) => (
                        <option key={orch.id} value={orch.id}>
                          {orch.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{t('common.save')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Global Settings Modal */}
        {showGlobalSettings && globalInstructions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t('orchestrations.globalSettings')}
                  </h2>
                  <button
                    onClick={() => setShowGlobalSettings(false)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('orchestrations.defaultTone')}
                    </label>
                    <select
                      value={globalInstructions.default_tone}
                      onChange={(e) =>
                        setGlobalInstructions({
                          ...globalInstructions,
                          default_tone: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      {TONE_OPTIONS.map((tone) => (
                        <option key={tone} value={tone}>
                          {t(`orchestrations.tones.${tone}`, tone)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('orchestrations.defaultPersona')}
                    </label>
                    <select
                      value={globalInstructions.default_persona}
                      onChange={(e) =>
                        setGlobalInstructions({
                          ...globalInstructions,
                          default_persona: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      {PERSONA_OPTIONS.map((persona) => (
                        <option key={persona} value={persona}>
                          {t(`orchestrations.personas.${persona}`, persona)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('orchestrations.customInstructions')}
                  </label>
                  <textarea
                    value={globalInstructions.custom_instructions || ''}
                    onChange={(e) =>
                      setGlobalInstructions({
                        ...globalInstructions,
                        custom_instructions: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    rows={6}
                    placeholder={t('orchestrations.customInstructionsPlaceholder')}
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowGlobalSettings(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveGlobalInstructions}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{t('common.save')}</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
